import {
  FC,
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DEBOUNCE_DELAY } from 'utils/constants/app/magic-numbers-app';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { findGames } from 'services/sportsbook/results';
import { Loading } from 'components/Loading';
import { Option, Select } from 'newcomponents/UI/Select';
import { useDebounce } from 'hooks/useDebounce';
import { setTeam } from 'store/actions/results-data';
import { getResultsSport } from 'store/selectors/results-data';
import './index.less';

type Props = {
  disabled?: boolean;
};

const what = {
  game: ['team1_id', 'team2_id', 'team1_name', 'team2_name']
};

const where: Record<string, any> = {
  game: {
    '@limit': 15,
    '@or': [
      {
        team1_name: {
          '@like': {
            eng: ''
          }
        }
      },
      {
        team2_name: {
          '@like': {
            eng: ''
          }
        }
      }
    ]
  }
};

const languagePrefix = SpringConfigs.LANGUAGE_PREFIX;

export const TeamFilter: FC<Props> = ({ disabled }): ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const resultsSport = useSelector(getResultsSport);
  const [value, setValue] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<
    { teamName: string; teamId: number }[]
  >([]);

  const containerRef =
    useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;

  useDebounce(
    () => {
      if (search?.length > 2 && search !== '') {
        let teamNumber = 2;

        if (
          where.game['@or'][0].team1_name &&
          where.game['@or'][1].team2_name
        ) {
          where.game['@or'][0].team1_name['@like'].eng = search;
          where.game['@or'][1].team2_name['@like'].eng = search;

          if (languagePrefix && languagePrefix !== 'eng') {
            where.game['@or'][0].team1_name['@like'][languagePrefix] = search;
            where.game['@or'][1].team2_name['@like'][languagePrefix] = search;
          }
        }

        findGames(
          { what: what, where: where },
          (data: {
            data: {
              game: Record<
                number,
                {
                  team1_name: string;
                  team1_id: number;
                  team2_name: string;
                  team2_id: number;
                }
              >;
            };
          }) => {
            const result: { teamName: string; teamId: number }[] = [];
            Object.values(data.data.game).forEach(game => {
              if (
                game.team1_name.toLowerCase().includes(search.toLowerCase())
              ) {
                teamNumber = 1;
              } else {
                teamNumber = 2;
              }

              !result.find(
                team =>
                  team.teamId ===
                  game[`team${teamNumber}_id` as 'team1_id' | 'team2_id']
              ) &&
                result.push({
                  teamName:
                    game[
                      `team${teamNumber}_name` as 'team1_name' | 'team2_name'
                    ],
                  teamId:
                    game[`team${teamNumber}_id` as 'team1_id' | 'team2_id']
                });
            });
            setSearchResults(result);
            setSearchLoading(false);
          }
        );
      } else {
        setSearchResults([]);
      }
    },
    DEBOUNCE_DELAY._500,
    [search]
  );

  const handleChange = useCallback((e: number | undefined) => {
    setValue(e);
    dispatch(setTeam(e));
  }, []);

  useEffect(() => {
    handleChange(undefined);
  }, [resultsSport]);

  return (
    <div className="teamFilter" ref={containerRef} data-testid="teamFilter">
      <div className="teamFilter__label">{t('sportsbook.team')}</div>
      <Select
        showSearch
        allowClear
        disabled={disabled}
        size="large"
        value={value}
        placeholder={t('sportsbook.searchTeamName')}
        defaultActiveFirstOption={false}
        showArrow={false}
        filterOption={false}
        onSearch={val => {
          setSearchLoading(true);
          setSearch(val);
        }}
        onChange={handleChange}
        getPopupContainer={() => containerRef.current}
        notFoundContent={
          search?.length > 2 && (
            <div className="teamFilter__empty">
              {searchLoading ? (
                <Loading size={EIconSize._16} />
              ) : (
                t('emptyMessages.noResultsTitle')
              )}
            </div>
          )
        }
        className="teamFilter__input"
      >
        {searchResults.map(result => (
          <Option
            data-testid="teamFilter_option"
            key={result.teamId}
            value={result.teamId}
          >
            {result.teamName}
          </Option>
        ))}
      </Select>
    </div>
  );
};
